import React, { FC, useMemo } from 'react';
import { ChatBox } from './components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Store } from '../../../redux';
import { ErrorLayout } from '../../../shared/components';
import { limit, or, orderBy, where } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { firebaseAuth } from '../../../shared/lib';
import { Theme, useMediaQuery } from '@mui/material';
import { DMPage } from './dm';
import { CHAT_PAGE_ROUTE, DM_PAGE_ROUTE } from './route.definitions';

interface ChatPageProps {
  avoidErrorMessage?: boolean;
  /**
   * When true, ChatPage is rendered inside an already-constrained
   * flex container (e.g. the desktop split-pane section). The component
   * skips its own viewport-height wrapper so it fills the parent instead.
   */
  embedded?: boolean;
}

export const ChatPage: FC<ChatPageProps> = ({
  avoidErrorMessage = false,
  embedded = false,
}) => {
  const [params] = useSearchParams();
  const receiverID = params.get('receiverID');
  const { room } = useSelector((state: Store) => state.inPlayReducer.gameInfo);

  const [user] = useAuthState(firebaseAuth);

  const queryConstraint = useMemo(() => {
    if (!receiverID?.length && !room?.roomID) {
      return [];
    }

    if (!user) return [];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const qc: any[] = [orderBy('createdAt', 'desc'), limit(50)];
    if (receiverID) {
      qc.push(
        or(
          where('threadID', '==', [receiverID, user.uid]),
          where('threadID', '==', [user.uid, receiverID])
        )
      );
      return qc;
    }
    if (room?.roomID) {
      qc.push(where('groupID', '==', room.roomID));

      return qc;
    }

    return [];
  }, [receiverID, room?.roomID, user]);

  if (!receiverID?.length && !room?.roomID && !avoidErrorMessage) {
    return (
      <ErrorLayout
        outerBoxProps={{ height: 'auto' }}
        texts={{
          defaultError: `Missing user info to start a chat`,
        }}
      />
    );
  }

  const chatBox = (
    <ChatBox
      outerBoxProps={{ className: 'flex flex-col flex-1 min-h-0' }}
      sendMessageProps={{
        receiverID: receiverID || '',
        roomID: room?.roomID || '',
      }}
      queryConstraint={queryConstraint}
    />
  );

  if (embedded) {
    return chatBox;
  }

  return (
    <div
      className="w-full min-w-0 flex flex-col min-h-0 overflow-hidden"
      style={{
        // Height already excludes the iOS safe-area insets, so we do NOT
        // add paddingBottom on top of that — doing both caused a ~34px
        // dead band under the message input on iPhone.
        height:
          'calc(100dvh - 60px - env(safe-area-inset-top) - var(--kb-safe-bottom, env(safe-area-inset-bottom)) - var(--kb-inset, 0px))',
        transition: 'height var(--kb-transition, 0ms linear)',
      }}
    >
      {chatBox}
    </div>
  );
};

/**
 * Desktop / tablet split-pane layout:
 * - Left column: DM threads list
 * - Right column: active chat (or empty-state hint)
 *
 * On mobile a separate single-pane flow is used (see BhabiDMPage).
 */
export const LgScreenChatPage: FC = () => {
  const [search] = useSearchParams();
  const receiverID = search.get('receiverID');
  const missingUID = !receiverID;
  const nav = useNavigate();

  const onClickThread = (id?: string) => () => {
    if (!id) {
      return;
    }

    const sp = new URLSearchParams({ receiverID: id }).toString();
    nav(DM_PAGE_ROUTE.ABSOLUTE_PATH + `?${sp}`);
  };

  return (
    <div
      className="
        container mx-auto
        grid
        grid-cols-[minmax(260px,320px)_1fr]
        gap-3
        px-2 sm:px-4
      "
      style={{
        height:
          'calc(100dvh - 60px - env(safe-area-inset-top) - var(--kb-safe-bottom, env(safe-area-inset-bottom)) - var(--kb-inset, 0px))',
        transition: 'height var(--kb-transition, 0ms linear)',
      }}
    >
      {/* Left pane: thread list */}
      <aside
        className="
          min-h-0 overflow-hidden rounded-2xl
          bg-bg-primary/60 dark:bg-nord-1/60
          border border-border-light dark:border-nord-3
          backdrop-blur-sm
        "
      >
        <DMPage
          onClickThread={onClickThread}
          activeThreadID={receiverID}
          textFieldProps={{ sx: { width: '100%' } }}
        />
      </aside>

      {/* Right pane: active chat */}
      <section
        className="
          min-h-0 overflow-hidden rounded-2xl
          bg-bg-primary/60 dark:bg-nord-1/60
          border border-border-light dark:border-nord-3
          backdrop-blur-sm
          flex flex-col
        "
      >
        {missingUID ? (
          <div className="flex-1 flex items-center justify-center px-6">
            <div className="text-center max-w-sm">
              <div className="mx-auto mb-4 w-16 h-16 rounded-2xl bg-gradient-to-br from-nord-8/30 to-nord-15/30 flex items-center justify-center text-3xl">
                💬
              </div>
              <h2 className="text-lg font-bold font-gaming text-nord-0 dark:text-nord-6">
                Pick a conversation
              </h2>
              <p className="text-sm text-nord-3 dark:text-nord-4 mt-2">
                Select a user from the list to open the thread, or search to
                start a new chat.
              </p>
            </div>
          </div>
        ) : (
          <ChatPage avoidErrorMessage embedded />
        )}
      </section>
    </div>
  );
};

export const BhabiDMPage: FC = () => {
  const isMobile = useMediaQuery<Theme>(theme => theme.breakpoints.down('sm'));
  const nav = useNavigate();

  // On mobile we navigate to the full-screen single-pane chat. On
  // desktop / tablet the split-pane is already wired to render the active
  // chat in the right column based on the receiverID search param —
  // navigating to /dm/chat would unmount the sidebar, which is exactly
  // what we don't want on bigger screens.
  const onClickThread = (id?: string) => () => {
    if (!id) return;
    const sp = new URLSearchParams({ receiverID: id }).toString();
    nav(
      (isMobile ? CHAT_PAGE_ROUTE.ABSOLUTE_PATH : DM_PAGE_ROUTE.ABSOLUTE_PATH) +
        `?${sp}`
    );
  };

  return !isMobile ? (
    <LgScreenChatPage />
  ) : (
    <div
      className="w-full min-w-0 overflow-hidden flex flex-col"
      style={{
        height:
          'calc(100dvh - 60px - env(safe-area-inset-top) - var(--kb-safe-bottom, env(safe-area-inset-bottom)) - var(--kb-inset, 0px))',
        transition: 'height var(--kb-transition, 0ms linear)',
      }}
    >
      <DMPage onClickThread={onClickThread} />
    </div>
  );
};
